import { toast } from '@myosh/odin-components';
import { useCallback, useEffect, useId, useState } from 'react';
import type { BowtieRequestWithId } from '../api/@types/enhanced-bowtie-api.types';
import { useBowtieSuggestJobPostMutation } from '../api/enhanced/enhanced-bowtie-api';
import type { BowtieSuggestPostApiArg, BowtieSuggestPostApiResponse } from '../api/generated/bowtie-api';
import { useGetAIBackgroundJobResult } from './use-get-ai-background-job-result';

/**
 * Custom hook to handle AI analysis for bowtie data.
 *
 * @param {Function} onAIAnalysisReady - Callback function to be called when AI analysis is ready. Receives the analysis text as an argument.
 *
 * @returns {Object} - An object containing:
 *   - `runAIAnalysis` (Function): Function to trigger the AI analysis.
 *   - `isLoading` (boolean): Indicates whether the AI analysis is currently loading.
 *
 * @example
 * const { runAIAnalysis, isLoading } = useAIAnalysis((text) => {
 *   console.log('AI Analysis Result:', text);
 * });
 *
 * // Trigger AI analysis
 * runAIAnalysis();
 */
export const useAIAnalysis = (onAIAnalysisReady: (text: string) => void, flowBowtieDto: BowtieRequestWithId) => {
  const toastId = useId();
  const [jobId, setJobId] = useState<string>();

  const [triggerSuggestPostJob, { isLoading }] = useBowtieSuggestJobPostMutation();

  const {
    data: backgroundJobResult,
    loading: backgroundJobLoading,
    error: backgroundJobError,
  } = useGetAIBackgroundJobResult({
    jobId: jobId!,
    setJobId: setJobId,
  });

  const loading = isLoading || backgroundJobLoading;

  const resetJob = useCallback(() => setJobId(undefined), []);

  useEffect(() => {
    if (backgroundJobResult && !loading) {
      const data = backgroundJobResult as unknown as BowtieSuggestPostApiResponse;
      onAIAnalysisReady(data.suggestion_text);
      resetJob();
      toast.success('AI Analysis Complete', { id: toastId });
    } else if (backgroundJobError && !loading) {
      resetJob();
      toast.error('Unable to generate ai suggestion, please try again later.', { id: toastId });
    }
  }, [loading, backgroundJobResult, backgroundJobError]);

  const handleBowtieAIAnalysisClick = useCallback(async () => {
    const payload = generateFlowSuggestPayload(flowBowtieDto);
    toast.loading('Running AI Analysis...', { id: toastId });
    triggerSuggestPostJob(payload)
      .unwrap()
      .then((jobId) => setJobId(jobId))
      .catch(() => {
        resetJob();
        toast.error('Unable to generate ai suggestion, please try again later.', { id: toastId });
      });
  }, [flowBowtieDto]);

  return {
    runAIAnalysis: handleBowtieAIAnalysisClick,
    isLoading: loading,
  };
};

// util
const generateFlowSuggestPayload = (bowtieData: BowtieRequestWithId, term?: string): BowtieSuggestPostApiArg => {
  return {
    bowtieCorrectionRequest: {
      bowtie: {
        ...bowtieData,
        term: term ?? '',
      },
      control_types: [''],
    },
  };
};
